import { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import { Box, Text } from '@theme-ui/components';
import isEmpty from 'lodash.isempty';

import { Button } from '@app/components/Button';
import { rem } from '@app/styles/theme/layout';

import type { ReturnRequestQueryVariables } from '../__graphql__/ReturnRequest';
import type { OrderHistoryOrderFragment } from '../../__graphql__/OrderHistory';
import useReturnRequestReasons from '../hooks/useOrderReturnReasons';
import messages from '../ReturnRequest.messages';
import {
  AdditionalInfoInput,
  FormError,
  FormErrorMessage,
  FormLoading,
  ProductCheckboxInput,
  RemainingChars,
  ReturnReasonRadioInput,
} from './form';

export type OrderProduct = {
  id: string;
  status: string | null;
  productSku: string;
  productName: string | null;
  eligibleForReturn: boolean | null;
  returnStatus: string | null;
};

export interface ReturnRequestFormData {
  productsToReturn: string[];
  returnReason: string;
  additionalInfo?: string;
}

type SubmitPayload = Omit<
  ReturnRequestQueryVariables,
  'orderUid' | 'contactEmail'
>;

interface ReturnRequestFormProps {
  products?: OrderHistoryOrderFragment['items'];
  onSubmitSuccess: (submitPayload: SubmitPayload) => void;
  requestLoading: boolean;
  requestError: boolean;
}

const formDefaultValues: ReturnRequestFormData = {
  productsToReturn: [],
  returnReason: '',
  additionalInfo: '',
};

const ReturnRequestForm = (props: ReturnRequestFormProps) => {
  const { products, onSubmitSuccess, requestLoading } = props;
  const {
    loading: reasonsLoading,
    returnReasons,
    error: reasonsError,
  } = useReturnRequestReasons();

  const methods = useForm<ReturnRequestFormData>({
    defaultValues: formDefaultValues,
  });

  const locizeKeyToZendeskReason = (locizeKey: string) => {
    const reason = locizeKey.split('.')[2].replace(/reason/g, '');
    return reason || locizeKey;
  };

  const {
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = methods;

  const onSubmit = handleSubmit((data) => {
    onSubmitSuccess({
      returnReason: locizeKeyToZendeskReason(data.returnReason),
      commentText: data.additionalInfo || 'No additional info submitted',
      items: data.productsToReturn.map((id) => ({
        order_item_uid: id,
        quantity_to_return: 1,
      })),
    });
  });

  useEffect(() => {
    // if there is only one product - preselect it
    if (products?.length === 1 && products[0]) {
      setValue('productsToReturn', [products[0].id]);
    }
  }, [products, setValue]);

  const additionalInfoLength = watch('additionalInfo')?.length || 0;
  const charsRemaining = 1000 - additionalInfoLength;

  const showGeneralError =
    reasonsError ||
    isEmpty(returnReasons) ||
    !returnReasons ||
    isEmpty(products);

  if (reasonsLoading || requestLoading) return <FormLoading />;
  if (showGeneralError) return <FormError />;

  return (
    <FormProvider {...methods}>
      <form onSubmit={onSubmit}>
        <Box>
          <Text sx={{ fontWeight: 'bold', fontSize: rem(14) }}>
            <FormattedMessage {...messages.formProductReturn} />
          </Text>
          {products?.map((product) => (
            <ProductCheckboxInput product={product} key={product?.id} />
          ))}
          <FormErrorMessage fieldName="productsToReturn" />
        </Box>

        <Box>
          <Text sx={{ fontWeight: 'bold', fontSize: rem(14) }}>
            <FormattedMessage {...messages.formReturnReason} />
          </Text>
          <Box ml={rem(-18)}>
            {returnReasons.map((reason, idx) => (
              <ReturnReasonRadioInput returnReason={reason} key={idx} />
            ))}
          </Box>

          <FormErrorMessage fieldName="returnReason" />
        </Box>

        <Box>
          <Box m={`${rem(20)} 0 ${rem(8)} 0`}>
            <AdditionalInfoInput />
            <RemainingChars charsRemaining={charsRemaining} />
          </Box>
          <FormErrorMessage fieldName="additionalInfo" />
        </Box>

        <Box
          pt={errors.additionalInfo ? 8 : 20}
          sx={{ display: 'flex', justifyContent: 'flex-end' }}
        >
          <Button
            type="primary"
            isLoading={isSubmitting}
            data-testid="return-request-form-submit"
            onClick={onSubmit}
          >
            <FormattedMessage {...messages.formSubmit} />
          </Button>
        </Box>
      </form>
    </FormProvider>
  );
};

export default ReturnRequestForm;
